import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Map } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { documentsService } from '../../../services/documentsService';
import { useAuthStore } from '../../../stores/authStore';
import { PDFViewer } from '../../../components/documents/PDFViewer';
import type { SiteDocument } from '../../../types/api';
import { gatePeriod, pickInspectorDocuments } from './inspectorDocuments';
import { BmpMapUploadButton } from './BmpMapUploadButton';

/**
 * The documents an inspector needs on the ground (Jim 2026-10-07): the BMP
 * map — the engineer's original and every updated map captured at the
 * inspection gate — and the site notice. Permits, the SWPPP and reports stay
 * in the Hub. Opens through the same page-drawing viewer as the Hub
 * Documents tab, so a PDF renders on a phone.
 */
export function LiteSiteDocuments({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [viewing, setViewing] = useState<{
    doc: SiteDocument;
    url: string;
  } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['site-documents', siteId, 'inspector'],
    queryFn: () =>
      documentsService.getSiteDocuments(siteId, {
        size: 200,
        scope: 'inspector',
      }),
    enabled: !!siteId,
  });
  const { bmpMaps, siteNotices } = pickInspectorDocuments(data?.items ?? []);

  const open = async (doc: SiteDocument) => {
    setOpening(doc.id);
    setOpenError(null);
    try {
      const { url } = await documentsService.getDocumentUrl(doc.id);
      const withToken =
        accessToken && url.includes('/file')
          ? `${url}${url.includes('?') ? '&' : '?'}token=${accessToken}`
          : url;
      setViewing({ doc, url: withToken });
    } catch {
      setOpenError("Couldn't open that document. Try again.");
    } finally {
      setOpening(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading documents…
      </div>
    );
  }
  if (isError) {
    return (
      <p className="p-4 text-sm text-red-600 dark:text-red-400">
        Couldn't load this site's documents.
      </p>
    );
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-700">
      <Group
        title="BMP map"
        empty="No BMP map on file"
        docs={bmpMaps}
        icon={Map}
        action={<BmpMapUploadButton siteId={siteId} />}
        opening={opening}
        onOpen={open}
        subtitle={(d) => {
          const period = gatePeriod(d);
          if (period === null) return 'Original';
          // The day it was uploaded, not just the month (Jim 10/14)
          const when = d.created_at
            ? format(parseISO(d.created_at), 'MMM d, yyyy')
            : period
              ? monthLabel(period)
              : '';
          return ['Updated', when, d.uploaded_by_name ?? '']
            .filter(Boolean)
            .join(' · ');
        }}
      />
      <Group
        title="Site notice"
        empty="No site notice on file"
        docs={siteNotices}
        icon={FileText}
        opening={opening}
        onOpen={open}
        subtitle={(d) =>
          d.created_at
            ? `Added ${format(parseISO(d.created_at), 'MMM d, yyyy')}`
            : ''
        }
      />
      {openError && (
        <p className="px-4 py-2 text-xs text-red-600 dark:text-red-400">
          {openError}
        </p>
      )}

      {viewing && (
        <PDFViewer
          url={viewing.url}
          documentId={viewing.doc.id}
          title={viewing.doc.title}
          mimeType={viewing.doc.mime_type}
          fileName={viewing.doc.file_name}
          tools="inspector"
          orientation={{
            rotation: viewing.doc.rotation ?? 0,
            flip: !!viewing.doc.flip,
          }}
          onSaveOrientation={async (o) => {
            await documentsService.updateDocument(viewing.doc.id, o);
            queryClient.invalidateQueries({
              queryKey: ['site-documents', siteId],
            });
          }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return format(new Date(y, m - 1, 1), 'MMM yyyy');
}

function Group({
  title,
  empty,
  docs,
  icon: Icon,
  opening,
  onOpen,
  subtitle,
  action,
}: {
  title: string;
  empty: string;
  docs: SiteDocument[];
  icon: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
  opening: string | null;
  onOpen: (doc: SiteDocument) => void;
  subtitle: (doc: SiteDocument) => string;
}) {
  return (
    <section className="px-4 py-3" aria-label={title}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 pt-1">
          {title}
          {docs.length > 1 && (
            <span className="ml-1 font-normal normal-case tracking-normal">
              ({docs.length})
            </span>
          )}
        </h3>
        {action}
      </div>
      {docs.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {docs.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onOpen(d)}
                disabled={opening === d.id}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-700/40 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-left disabled:opacity-60"
              >
                {opening === d.id ? (
                  <Loader2 className="w-5 h-5 shrink-0 animate-spin text-primary-600" />
                ) : (
                  <Icon className="w-5 h-5 shrink-0 text-primary-600 dark:text-primary-400" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900 dark:text-white truncate">
                    {d.title}
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                    {subtitle(d)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
